import { useCallback, useMemo } from "react";
import type Conversation from "admin/types/interfaces/conversation";
import type { ReviewStatus } from "admin/types/interfaces/conversation";
import { safelyCamelize } from "~/utils/object";

const { VITE_ADMIN_CONVERSATIONS_API } = import.meta.env;

interface UseReviewStatusReturn {
  updateReviewStatus: (newStatus: ReviewStatus) => Promise<Conversation | undefined>;
}

/**
 * Custom hook for updating conversation review status.
 *
 * @param conversationId - The ID of the conversation
 * @returns Object containing update function
 */
const useReviewStatus = (conversationId: string | undefined): UseReviewStatusReturn => {
  const baseUrl = useMemo(
    () =>
      conversationId ? `${VITE_ADMIN_CONVERSATIONS_API}/${conversationId}/review-status` : null,
    [conversationId]
  );

  // Update review status
  const updateReviewStatus = useCallback(
    async (newStatus: ReviewStatus): Promise<Conversation | undefined> => {
      if (!baseUrl) {
        console.warn("updateReviewStatus called without conversationId");
        return;
      }

      const response = await fetch(baseUrl, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ review_status: newStatus }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update review status: ${response.status}`);
      }

      const rawConversation = await response.json();
      return safelyCamelize(rawConversation) as Conversation;
    },
    [baseUrl]
  );

  return {
    updateReviewStatus,
  };
};

export default useReviewStatus;
